import React, { useState, useEffect, useRef } from 'react'
import { motion, useInView, useScroll, useTransform, useMotionValueEvent } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Phone } from 'lucide-react'
import Lenis from 'lenis'

const ease = [0.16, 1, 0.3, 1] as const

const colors = {
  teal: '#145F6C',
  tealDark: '#0D3135',
  copper: '#AB9381',
}

const images = {
  logo: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson-stacked-dark.png',
  logoWhite: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Primary_Logo_White.png',
}

const BOOKING_CALENDAR_URL = 'https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2'

// ============================================
// SMOOTH SCROLL (desktop only)
// ============================================
let lenisInstance: Lenis | null = null

export const SmoothScroll: React.FC = () => {
  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (isTouchDevice || prefersReducedMotion) return

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
      touchMultiplier: 0,
    })

    lenisInstance = lenis

    function raf(time: number) {
      lenis.raf(time)
      requestAnimationFrame(raf)
    }
    requestAnimationFrame(raf)

    return () => {
      lenis.destroy()
      lenisInstance = null
    }
  }, [])

  return null
}

// ============================================
// SHARED NAVBAR
// ============================================
const ScrollProgressBar: React.FC = () => {
  const { scrollYProgress } = useScroll()
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1])

  return (
    <motion.div
      style={{ scaleX, transformOrigin: 'left' }}
      className="fixed top-0 left-0 right-0 h-[2px] z-[60]"
      aria-hidden
    >
      <div className="h-full w-full" style={{ backgroundColor: colors.copper }} />
    </motion.div>
  )
}

export const Navbar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [navVisible, setNavVisible] = useState(true)
  const { scrollY } = useScroll()

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const prev = scrollY.getPrevious() ?? 0
    if (menuOpen) return
    if (latest < 80) { setNavVisible(true); return }
    setNavVisible(latest < prev)
  })

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  const textClass = 'text-white/80 hover:text-white'

  return (
    <>
      <ScrollProgressBar />
      <div dangerouslySetInnerHTML={{ __html: '<!-- Cloudflare header check -->' }} />
      <motion.header
        initial={false}
        animate={{ y: navVisible || menuOpen ? 0 : '-100%' }}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="fixed top-0 left-0 right-0 z-50"
        style={{ backgroundColor: '#081E22ee', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <Link to="/" className="relative z-10 flex-shrink-0 flex items-center">
              <img
                src={images.logoWhite}
                alt="Hudson My Home"
                className="h-8 lg:h-9 w-auto object-contain object-left transition-opacity duration-300"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-0">
              <Link to="/" className={`text-sm tracking-wide px-4 py-2.5 transition-all duration-300 ${textClass}`}>
                Home
              </Link>
              <div className="relative group">
                <button
                  type="button"
                  className={`text-sm tracking-wide px-4 py-2.5 transition-all duration-300 flex items-center gap-1 ${textClass}`}
                >
                  Services
                  <svg className="w-3 h-3 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute top-full left-0 mt-1 w-56 bg-white shadow-lg border border-grey-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/membership" className="block px-4 py-3 text-sm text-grey-700 hover:bg-grey-50 transition-colors">Home Membership</Link>
                  <Link to="/refresh" className="block px-4 py-3 text-sm text-grey-700 hover:bg-grey-50 transition-colors">MyHome Refresh&#8482;</Link>
                  <Link to="/smart-start" className="block px-4 py-3 text-sm text-grey-700 hover:bg-grey-50 transition-colors">MyHome SmartStart&#8482;</Link>
                </div>
              </div>
              <Link to="/pricing" className={`text-sm tracking-wide px-4 py-2.5 transition-all duration-300 ${textClass}`}>Pricing</Link>
              <Link to="/about" className={`text-sm tracking-wide px-4 py-2.5 transition-all duration-300 ${textClass}`}>About</Link>
            </nav>

            <div className="hidden lg:flex items-center gap-5">
              <a href="tel:5152164433" className="text-sm tracking-wide text-white/70 hover:text-white transition-colors duration-300 flex items-center gap-1.5">
                <Phone className="w-4 h-4" />
                (515) 216-4433
              </a>
              <a
                href={BOOKING_CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 text-sm font-medium tracking-wide text-white px-5 py-3 transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: colors.copper }}
              >
                Schedule a Meeting
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>

            <div className="lg:hidden flex items-center gap-2">
              <a href="tel:5152164433" className="relative z-10 w-11 h-11 flex items-center justify-center text-white/80 hover:text-white transition-colors" aria-label="Call Hudson My Home">
                <Phone className="w-5 h-5" />
              </a>
              <button onClick={() => setMenuOpen(!menuOpen)} className="relative z-10 w-11 h-11 flex items-center justify-center" aria-label={menuOpen ? 'Close menu' : 'Open menu'}>
                <div className="w-6 h-5 flex flex-col justify-between">
                  <motion.span animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 8 : 0 }} className="w-full h-[1.5px] origin-center bg-white" />
                  <motion.span animate={{ opacity: menuOpen ? 0 : 1 }} className="w-full h-[1.5px] bg-white" />
                  <motion.span animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -8 : 0 }} className="w-full h-[1.5px] origin-center bg-white" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none' }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-40 lg:hidden overflow-y-auto"
        style={{ backgroundColor: colors.tealDark, paddingTop: '5.5rem' }}
      >
        <div className="flex flex-col items-center px-6 pb-12 gap-5">
          <Link to="/" onClick={() => setMenuOpen(false)} className="mb-2" aria-label="Hudson My Home home">
            <img src={images.logoWhite} alt="" className="h-9 w-auto object-contain mx-auto opacity-95" />
          </Link>
          <Link to="/" onClick={() => setMenuOpen(false)} className="text-xl font-serif text-white">Home</Link>
          <div className="text-center w-full max-w-sm">
            <p className="text-sm uppercase tracking-[0.15em] text-white/50 mb-3">Services</p>
            <div className="flex flex-col gap-2">
              <Link to="/membership" onClick={() => setMenuOpen(false)} className="text-base text-white/90">Home Membership</Link>
              <Link to="/refresh" onClick={() => setMenuOpen(false)} className="text-base text-white/90">MyHome Refresh&#8482;</Link>
              <Link to="/smart-start" onClick={() => setMenuOpen(false)} className="text-base text-white/90">MyHome SmartStart&#8482;</Link>
            </div>
          </div>
          <Link to="/pricing" onClick={() => setMenuOpen(false)} className="text-xl font-serif text-white">Pricing</Link>
          <Link to="/about" onClick={() => setMenuOpen(false)} className="text-xl font-serif text-white">About</Link>
          <a
            href={BOOKING_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            className="mt-4 inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-white"
            style={{ backgroundColor: colors.copper }}
          >
            Schedule a Meeting
            <ArrowRight className="w-4 h-4" />
          </a>
          <Link
            to="/quiz"
            onClick={() => setMenuOpen(false)}
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium border border-white/25 text-white/90"
          >
            Discover Your Archetype
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </motion.div>
    </>
  )
}

// ============================================
// SHARED FOOTER
// ============================================
interface FooterProps {
  variant?: 'dark' | 'light'
}

export const Footer: React.FC<FooterProps> = ({ variant = 'dark' }) => {
  const currentYear = new Date().getFullYear()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-50px" })
  const isDark = variant === 'dark'

  // Light variant - simple footer
  if (!isDark) {
    return (
      <footer data-testid="shared-footer" className="py-12 bg-white border-t border-grey-100">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <img src={images.logo} alt="Hudson" className="h-6" />
            <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-8">
              <Link to="/" className="text-sm text-grey-500 hover:text-grey-900 transition-colors">Home</Link>
              <Link to="/partners" className="text-sm text-grey-500 hover:text-grey-900 transition-colors">Partners</Link>
              <Link to="/builders" className="text-sm text-grey-500 hover:text-grey-900 transition-colors">Builders</Link>
            </div>
            <p className="text-sm text-grey-400">&copy; {currentYear} Hudson My Home</p>
          </div>
        </div>
      </footer>
    )
  }
  
  return (
    <footer ref={ref} data-testid="shared-footer" className="relative overflow-hidden" style={{ backgroundColor: '#081E22' }}>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D3135]/40 via-transparent to-transparent" />
      
      {/* Accent glow */}
      <div 
        className="absolute bottom-0 left-1/4 w-1/2 h-1/2 opacity-25"
        style={{ background: `radial-gradient(ellipse 80% 60% at 50% 100%, ${colors.teal}40, transparent)` }}
      />
      
      <div className="relative">
        {/* Main footer content */}
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Brand column */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease, delay: 0.2 }}
              className="lg:col-span-4"
            >
              <img src={images.logoWhite} alt="Hudson" className="h-8 mb-6" />
              <p className="text-base text-white/40 leading-[1.65] mb-8 max-w-xs">
                Private home management for homeowners who value their time above all else.
              </p>
              <div className="flex items-center gap-4">
                <span className="text-sm text-white/30">Follow us</span>
                <div className="flex gap-3">
                  <a href="https://www.instagram.com/hudsonmyhome/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition-all duration-300" aria-label="Instagram">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="https://www.facebook.com/hudsonmyhome" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition-all duration-300" aria-label="Facebook">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                  </a>
                  <a href="https://www.linkedin.com/company/hudson-my-home/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition-all duration-300" aria-label="LinkedIn">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                  </a>
                </div>
              </div>
            </motion.div>
            
            {/* Links columns */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease, delay: 0.3 }}
              className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8"
            >
              <div>
                <p className="text-sm tracking-[0.15em] uppercase text-white/30 mb-6">Product</p>
                <ul className="space-y-4">
                  <li><Link to="/membership" className="text-sm text-white/50 hover:text-white transition-colors duration-300">Membership</Link></li>
                  <li><Link to="/pricing" className="text-sm text-white/50 hover:text-white transition-colors duration-300">Pricing</Link></li>
                  <li><Link to="/quiz" className="text-sm text-white/50 hover:text-white transition-colors duration-300">Archetype Quiz</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-sm tracking-[0.15em] uppercase text-white/30 mb-6">Partners</p>
                <ul className="space-y-4">
                  <li><Link to="/partners" className="text-sm text-white/50 hover:text-white transition-colors duration-300">Real Estate Agents</Link></li>
                  <li><Link to="/builders" className="text-sm text-white/50 hover:text-white transition-colors duration-300">Luxury Builders</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-sm tracking-[0.15em] uppercase text-white/30 mb-6">Resources</p>
                <ul className="space-y-4">
                  <li><Link to="/about" className="text-sm text-white/50 hover:text-white transition-colors duration-300">About Us</Link></li>
                  <li><Link to="/faqs" className="text-sm text-white/50 hover:text-white transition-colors duration-300">FAQ</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-sm tracking-[0.15em] uppercase text-white/30 mb-6">Contact</p>
                <ul className="space-y-4">
                  <li><a href="tel:+15152164433" className="text-sm text-white/50 hover:text-white transition-colors duration-300">(515) 216-4433</a></li>
                  <li><a href="mailto:info@hudsonmyhome.com" className="text-sm text-white/50 hover:text-white transition-colors duration-300">info@hudsonmyhome.com</a></li>
                  <li><span className="text-sm text-white/50">Urbandale, Iowa</span></li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-white/10">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-white/30">&copy; {currentYear} Hudson My Home, LLC. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

